import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FailbackRow {
  readonly failbackId: string;
  readonly failbackCode: string;
  readonly affectedService: string;
  readonly status: string;
  readonly dataValidated: boolean;
  readonly syncValidated: boolean;
  readonly serviceValidated: boolean;
  readonly approvedBy: string | null;
}

const STATUSES = ['', 'INITIATED', 'IN_PROGRESS', 'DATA_VALIDATION', 'COMPLETED', 'FAILED'];

/**
 * M31 — Failback Management Screen (§14 / §31). Register of failback operations
 * with the three-part validation gate (data / sync / service) that must all pass
 * before approval, plus approval and completion status. Supports initiating a
 * failback, recording the validation gate, approving (SoD: separate approver),
 * completing and marking a failure — each wired to the guarded /v1/bcm/failbacks
 * write API. Filterable by status.
 */
export function FailbackManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FailbackRow[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newService, setNewService] = useState('');
  const [failDetail, setFailDetail] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FailbackRow[] }>(`/v1/bcm/failbacks${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, reload]);

  const gate = (r: FailbackRow) => (r.dataValidated && r.syncValidated && r.serviceValidated ? 'READY' : 'PENDING');

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m31.failback.err403')
      : (err as Error).message;

  const initiate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newService.trim()) { setActionError(t('m31.failback.errService')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/failbacks`, {
        method: 'POST',
        body: { affectedService: newService.trim() },
        token,
      });
      setNotice(t('m31.failback.noticeInitiated', { service: newService.trim() }));
      setNewService('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: FailbackRow, path: string, label: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/failbacks/${r.failbackId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m31.failback.actNotice', { code: r.failbackCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const fail = async (r: FailbackRow): Promise<void> => {
    const detail = (failDetail[r.failbackId] ?? '').trim();
    if (!detail) { setActionError(t('m31.failback.errFail')); return; }
    await act(r, 'fail', t('m31.failback.markedFailed'), { errorDetail: detail });
  };

  return (
    <main aria-label="failback-management">
      <h1>{t('m31.failback.title')}</h1>

      <section aria-label="initiate-failback">
        <h2>{t('m31.failback.initiateHeading')}</h2>
        <form aria-label="failback-form" onSubmit={(e) => void initiate(e)}>
          <label>{t('m31.failback.fieldService')}<input aria-label="field-affected-service" value={newService} onChange={(e) => setNewService(e.target.value)} /></label>
          <button type="submit" aria-label="submit-failback" disabled={busy}>{busy ? t('m31.failback.busyInitiate') : t('m31.failback.submitInitiate')}</button>
        </form>
        {actionError && <p role="alert" aria-label="failback-error">{actionError}</p>}
        {notice && <p aria-label="failback-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m31.failback.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m31.failback.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m31.failback.loading')}</p> : (
        <>
          <p aria-label="failback-count">{t('m31.failback.count', { count: rows.length })}</p>
          <table aria-label="failback-table">
            <thead><tr><th>{t('m31.failback.thCode')}</th><th>{t('m31.failback.thService')}</th><th>{t('m31.failback.thData')}</th><th>{t('m31.failback.thSync')}</th><th>{t('m31.failback.thServiceCol')}</th><th>{t('m31.failback.thGate')}</th><th>{t('m31.failback.thApproved')}</th><th>{t('m31.failback.thStatus')}</th><th>{t('m31.failback.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.failbackId} data-gate={gate(r)}>
                  <td>{r.failbackCode}</td><td>{r.affectedService}</td>
                  <td>{r.dataValidated ? '✓' : '—'}</td>
                  <td>{r.syncValidated ? '✓' : '—'}</td>
                  <td>{r.serviceValidated ? '✓' : '—'}</td>
                  <td aria-label={`gate-${r.failbackId}`}>{gate(r)}</td>
                  <td>{r.approvedBy ? 'YES' : 'NO'}</td>
                  <td>{r.status}</td>
                  <td>
                    {r.status !== 'COMPLETED' && r.status !== 'FAILED' && (
                      <>
                        {gate(r) !== 'READY' && (
                          <button type="button" aria-label={`validate-${r.failbackCode}`} onClick={() => void act(r, 'validate', t('m31.failback.validationRecorded'), { dataValidated: true, syncValidated: true, serviceValidated: true })}>{t('m31.failback.validate')}</button>
                        )}
                        {gate(r) === 'READY' && !r.approvedBy && (
                          <button type="button" aria-label={`approve-${r.failbackCode}`} onClick={() => void act(r, 'approve', t('m31.failback.approvedLabel'))}>{t('m31.failback.approveBtn')}</button>
                        )}
                        {r.approvedBy && (
                          <button type="button" aria-label={`complete-${r.failbackCode}`} onClick={() => void act(r, 'complete', t('m31.failback.completed'))}>{t('m31.failback.complete')}</button>
                        )}
                        <input aria-label={`fail-input-${r.failbackCode}`} placeholder={t('m31.failback.failPlaceholder')} value={failDetail[r.failbackId] ?? ''} onChange={(e) => setFailDetail((m) => ({ ...m, [r.failbackId]: e.target.value }))} />
                        <button type="button" aria-label={`fail-${r.failbackCode}`} onClick={() => void fail(r)}>{t('m31.failback.markFailed')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
